import {
  useRef,
  useState,
  useEffect,
  forwardRef,
  FocusEvent,
  ChangeEvent,
  useCallback,
} from "react";
import cn from "classnames";
import { mergeRefs } from "../common/utils/merge-refs";
import { MaskInput } from "../mask-input";
import { MaskConfig } from "../mask-input/types";
import {
  PHONE_MASK,
  RU_PHONE_CODE,
  DEFAULT_PLACEHOLDER,
  RU_PHONE_CODE_INTERNAL,
  PHONE_NUMBER_CARET_POSITION,
  MIN_LENGTH_WITH_ONE_DIGIT,
} from "./constants";
import {
  createMask,
  isFilledPhone,
  setCaretPosition,
  getRawPhoneValue,
  trimPhoneCodeValue,
  getRawPhoneNumberValue,
} from "./utils";
import { PhoneInputProps } from "./types";
import styles from "./styles.module.scss";

export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  (
    {
      onBlur,
      onFocus,
      onChange,
      className,
      value = "",
      placeholder = DEFAULT_PLACEHOLDER,
      dataTestId = "phone-input",
      ...restProps
    },
    forwardedRef
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [maskValue, setMaskValue] = useState(value);
    const [focused, setFocus] = useState(false);
    const [caretPosition, setCurrentCaretPosition] = useState(
      PHONE_NUMBER_CARET_POSITION
    );

    const rawMaskValue = getRawPhoneValue(maskValue);

    useEffect(() => {
      const rawValue = getRawPhoneValue(value);

      if (rawValue !== rawMaskValue) {
        setMaskValue(value);
      }
    }, [value]);

    const calculateLastPositionOfCaret = (
      event: ChangeEvent<HTMLInputElement>,
      rawPhoneValue: string
    ): number => {
      const lastDigit = rawPhoneValue.slice(-1);

      /* Плюс один, так как нам нужно выставить каретку на следующую позицию, после введенного числа */
      const lastCaretPosition = event.target.value.lastIndexOf(lastDigit) + 1;

      return lastCaretPosition;
    };

    const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
      /* Автоматически подставляем код страны при отсутствии значения */
      if (!rawMaskValue) {
        setMaskValue(() => DEFAULT_PLACEHOLDER);
      }

      setFocus(true);
      /* Для гарантированного выставления каретки на корректную позицию */
      requestAnimationFrame(() => {
        if (caretPosition && rawMaskValue) {
          const lastCaretPosition = calculateLastPositionOfCaret(
            event,
            rawMaskValue
          );

          setCaretPosition(inputRef, lastCaretPosition);
        } else {
          setCaretPosition(inputRef, PHONE_NUMBER_CARET_POSITION);
        }
      });

      onFocus?.(event);
    };

    const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
      /* Автоматически удаляем значение при отсутствии номера телефона для отображения placeholder */
      if (rawMaskValue === RU_PHONE_CODE) {
        setMaskValue("");
      }

      setFocus(false);
      onBlur?.(event);
    };

    const handlePrepareChange = useCallback(
      (maskValue: string, config: MaskConfig) => {
        const { rawValue: propValue, currentCaretPosition } = config;
        const rawPhoneValue = getRawPhoneValue(propValue);
        const rawPhoneNumberValue = getRawPhoneNumberValue(propValue);

        /* Если каретка находится перед кодом страны */
        if (currentCaretPosition < PHONE_NUMBER_CARET_POSITION) {
          /* Если введена 7 или 8 */
          if (
            rawPhoneValue.length <= MIN_LENGTH_WITH_ONE_DIGIT &&
            rawPhoneValue[0] === RU_PHONE_CODE
          ) {
            /* Перемещаем каретку на позицию PHONE_NUMBER_CARET_POSITION */
            setCaretPosition(inputRef, PHONE_NUMBER_CARET_POSITION);
            setMaskValue(DEFAULT_PLACEHOLDER);

            return DEFAULT_PLACEHOLDER;
          }
        }

        /* Предотвращаем переход каретки до границ кода страны после изменения значения */
        if (
          focused &&
          rawPhoneNumberValue &&
          rawPhoneNumberValue !== rawPhoneValue &&
          currentCaretPosition < PHONE_NUMBER_CARET_POSITION
        ) {
          setCaretPosition(inputRef, PHONE_NUMBER_CARET_POSITION);
        }

        /* Предотвращаем удаление кода страны в не заполненном поле */
        if (focused && (!rawPhoneValue || rawPhoneValue === RU_PHONE_CODE)) {
          const { conformedValue } = createMask(
            RU_PHONE_CODE,
            PHONE_MASK,
            config
          );

          setCaretPosition(inputRef, PHONE_NUMBER_CARET_POSITION);
          setMaskValue(conformedValue);

          return conformedValue;
        }

        /* Поддерживаем перезапись значения при вставке или передаче заполненного номера,
         * c заменой кода страны с 8 на 7
         */
        if (
          isFilledPhone(rawPhoneValue) &&
          (rawPhoneValue.startsWith(RU_PHONE_CODE) ||
            rawPhoneValue.startsWith(RU_PHONE_CODE_INTERNAL))
        ) {
          const phoneWithoutCode = trimPhoneCodeValue(rawPhoneValue);
          const { conformedValue } = createMask(
            RU_PHONE_CODE + phoneWithoutCode,
            PHONE_MASK,
            config
          );

          setMaskValue(conformedValue);

          return conformedValue;
        }

        /* Добавляем код страны к значению которое передается или вставляется в поле при условиях:
         * номер не заполнен полностью
         * номер начинается не с 7
         */
        if (
          rawPhoneNumberValue &&
          rawPhoneValue === rawPhoneNumberValue &&
          (!isFilledPhone(rawPhoneValue) ||
            !rawPhoneValue.startsWith(RU_PHONE_CODE))
        ) {
          const rawMaskValue = getRawPhoneValue(maskValue);

          /* Не позволяет изменить код страны при установке каретки перед кодом или префиксом,
           * переносим каретку в позицию ввода номера
           */
          if (
            rawMaskValue &&
            maskValue !== propValue &&
            currentCaretPosition < PHONE_NUMBER_CARET_POSITION
          ) {
            setCaretPosition(inputRef, PHONE_NUMBER_CARET_POSITION);
            setMaskValue(maskValue);

            return maskValue;
          }

          const { conformedValue } = createMask(
            RU_PHONE_CODE + rawPhoneValue,
            PHONE_MASK,
            config
          );

          setMaskValue(conformedValue);

          return conformedValue;
        }

        return maskValue;
      },
      [focused]
    );

    const handleChangeCaretPosition = (
      event: ChangeEvent<HTMLInputElement>,
      rawPhoneValue: string
    ) => {
      const selectionStart = event.target.selectionStart ?? 0;
      const lastCaretPosition = calculateLastPositionOfCaret(
        event,
        rawPhoneValue
      );

      /* Если курсор находится между "(" и "7", перемещаем его после скобки */
      if (selectionStart > 1 && selectionStart <= PHONE_NUMBER_CARET_POSITION) {
        setCaretPosition(inputRef, PHONE_NUMBER_CARET_POSITION);
      } else {
        setCurrentCaretPosition(lastCaretPosition);
      }
    };

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      const rawPhoneValue = getRawPhoneValue(value);

      /* Если значение пустое или только с кодом страны (например, "+7") */
      if (!rawPhoneValue || rawPhoneValue === RU_PHONE_CODE) {
        /* Устанавливаем значение по умолчанию с кодом страны */
        setMaskValue(DEFAULT_PLACEHOLDER);
        setCaretPosition(inputRef, PHONE_NUMBER_CARET_POSITION);

        return;
      }

      handleChangeCaretPosition(event, rawPhoneValue);

      setMaskValue(value);
      onChange?.(event, { phoneValue: rawPhoneValue });
    };

    return (
      <MaskInput
        type="tel"
        className={cn(styles.phone, className)}
        value={maskValue}
        mask={PHONE_MASK}
        placeholder={placeholder}
        onFocus={handleFocus}
        prepareChange={handlePrepareChange}
        onChange={handleChange}
        onBlur={handleBlur}
        ref={mergeRefs(inputRef, forwardedRef)}
        dataTestId={dataTestId}
        showMask
        {...restProps}
      />
    );
  }
);
